import { useMemo } from "react";
import useSWR from "swr";
import type { SupabaseClient } from "@supabase/supabase-js";
import { loadForecastPresentationInputs } from "@revelate/providers/supabase/forecast-presentation";
import { getAPI } from "@/lib/api";
import { getDealstageAvgTimesByCompanyId } from "@/lib/supabase";
import { supabaseClient } from "@/lib/supabase/client";
import { useAppStore } from "@/stores";
import { getForecastTimePeriodRange } from "../lib/risk";
import { useStateOfSalesV2Forecast } from "../state-of-sales-v2/useForecastData";
import { useForecastDiagnosisStory } from "../forecast-diagnosis/useForecastDiagnosisStory";
import { getSelectedPeriodMonths } from "../forecast-diagnosis/forecastDiagnosisContract";
import { getStateOfSalesV2ForecastPosition } from "../state-of-sales-v2/forecastPosition";
import { buildForecastHistory, buildPresentationSnapshot, projectFuturePipeline } from "./projection";
import { recoverFutureHistoryDates } from "./futureHistoryRecovery";
import { groupForecastOutcomes } from "./outcomes";
import { loadForecastRunHistory } from "./forecastRunHistory";
import { buildReconstructedForecastMovement } from "./reconstructedMovement";
import { FORECAST_COMPARISON_DAYS } from "./copy";

export function useForecastExperience() {
  const user = useAppStore(state => state.currentUser);
  const model = useStateOfSalesV2Forecast({ view: "forecast", requireCanonicalForecast: true });
  const story = useForecastDiagnosisStory(model);
  const companyId = user?.company_id;
  const enabled = Boolean(companyId && model.forecast && !model.isLoading && !model.error);
  const context = useSWR(enabled ? ["forecast-presentation-inputs", companyId] : null,
    async ([, id]) => {
      const [inputs, pipelines, stageTimes] = await Promise.all([
        loadForecastPresentationInputs(supabaseClient as unknown as SupabaseClient, Number(id)),
        getAPI(`providers/hubspot/pipelines/${id}`), getDealstageAvgTimesByCompanyId(Number(id)),
      ]);
      return { ...inputs, pipelines, stageTimes, capturedAt: new Date().toISOString() };
    }, { revalidateOnFocus: false, revalidateOnReconnect: false, errorRetryCount: 0 });
  const artifact = model.expectedCloseV2CompanyForecast;
  const run = useSWR(enabled && artifact?.asOf ? ["forecast-presentation-run", companyId, artifact.asOf] : null,
    async ([, id, asOf]) => {
      const result = await supabaseClient.from("expected_close_v2_runs").select("id,as_of,model_version,status")
        .eq("company_id", Number(id)).eq("as_of", String(asOf)).eq("status", "completed").single();
      if (result.error || result.data.model_version !== artifact?.modelVersion) throw new Error("The active forecast run could not be verified.");
      return result.data;
    }, { revalidateOnFocus: false, errorRetryCount: 0 });
  const historyResult = useMemo(() => {
    if (!context.data) return null;
    try { return buildForecastHistory(context.data); } catch { return null; }
  }, [context.data]);
  const snapshotResult = useMemo(() => {
    if (!context.data || !artifact || !model.companyCurrency?.code) return { snapshot: null, error: null };
    try {
      return { snapshot: buildPresentationSnapshot({ ...context.data, preparedHistory: historyResult ?? undefined, artifact, runId: run.data?.id ?? "unverified",
        modelVersion: artifact.modelVersion ?? "unverified", asOf: artifact.asOf ?? context.data.capturedAt,
        currencyCode: model.companyCurrency.code }), error: null };
    } catch {
      return { snapshot: null, error: new Error("The history could not be prepared. Existing pipeline remains available.") };
    }
  }, [context.data, historyResult, artifact, model.companyCurrency, run.data?.id]);
  const currentSnapshot = snapshotResult.snapshot;
  const asOf = useMemo(() => new Date(context.data?.capturedAt ?? artifact?.generatedAt ?? Date.now()), [context.data?.capturedAt, artifact?.generatedAt]);
  const range = useMemo(() => getForecastTimePeriodRange(model.period, asOf), [model.period, asOf]);
  const months = useMemo(() => getSelectedPeriodMonths(range.start, range.end), [range]);
  const futureHistory = useMemo(() => historyResult && context.data ? projectFuturePipeline(historyResult, asOf, range, model.filters,
    { boundedHistory: true, recoveredById: recoverFutureHistoryDates(historyResult, context.data.rawDeals, asOf) }) : null,
    [historyResult, context.data, asOf, range, model.filters]);
  const historical = model.period === "prev_quarter";
  const future = historical ? 0 : futureHistory?.future.value ?? null;
  const position = model.forecast ? getStateOfSalesV2ForecastPosition(model.forecast, model.period, model.accumulated, model.chartData) : null;
  const outcome = position ? position.forecast + (future ?? 0) : null;
  const currentWon = position?.won ?? 0;
  const previousSnapshot = useSWR(enabled && currentSnapshot && run.data && artifact?.asOf && artifact.modelVersion
    ? ["forecast-reconstructed-comparison", companyId, artifact.asOf, artifact.modelVersion, FORECAST_COMPARISON_DAYS] : null,
    async () => loadForecastRunHistory(supabaseClient as unknown as SupabaseClient, Number(companyId), artifact!.asOf!, artifact!.modelVersion!),
    { revalidateOnFocus: false, revalidateOnReconnect: false, errorRetryCount: 0 });
  const movement = useMemo(() => {
    if (!previousSnapshot.data || !currentSnapshot || !context.data || outcome === null || model.forecastEnrichmentLoading) return null;
    try {
      return buildReconstructedForecastMovement({ current: currentSnapshot, previous: previousSnapshot.data, rawDeals: context.data.rawDeals,
        currentOutcome: outcome, currentWon, currentFuture: future, months, scope: model.filters, range, pipelines: context.data.pipelines?.results ?? [] });
    } catch {
      return { available: false as const, reason: "The forecast comparison could not be prepared. Refresh to try again." };
    }
  }, [previousSnapshot.data, currentSnapshot, context.data, outcome, currentWon, future, months, model.filters, range, model.forecastEnrichmentLoading]);
  const outcomes = useMemo(() => artifact ? groupForecastOutcomes(model.filteredEvaluatedForecastOpenDeals, artifact, months, range) : null,
    [artifact, model.filteredEvaluatedForecastOpenDeals, months, range]);
  return { model, story, position, range, months, asOf, context, futureHistory, future, projectionError: snapshotResult.error,
    complete: historical || future !== null, currentSnapshot, previousSnapshot, movement, outcomes,
    outcome };
}
